/* global BigInt */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ethers } from 'ethers';
import lbDialog from '../UI/Modals/lbDialog';
import { useWriteContract } from 'wagmi';
import { governanceAddress, provider } from '../../.config';
import governanceAbi from '../../abi/LuckblocksGovernance.json';
import { useT } from '../../i18n';

const REFRESH_MS = 30000;
const PAGE_SIZE = 25;
// ProposalState of the contract, in order
export const STATES = ['active', 'defeated', 'succeeded', 'executed', 'expired', 'canceled', 'queued', 'vetoed'];
// CallLevel of the contract: what a proposal may call and how long it waits after the vote
export const CALL_LEVELS = ['none', 'standard', 'critical'];

const settle = (promise, fallback = null) => Promise.resolve(promise).catch(() => fallback);
const days = (seconds) => seconds.toNumber() / 86400;

/** Proposals of the node council plus the connected wallet's voting power and votable nodes */
export function useGovernance(address) {
  const t = useT();
  const { writeContractAsync } = useWriteContract();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState(null);
  const [votingPower, setVotingPower] = useState(0);
  const [stakedPower, setStakedPower] = useState(0);
  const [proposals, setProposals] = useState([]);

  const governance = useMemo(
    () => (governanceAddress ? new ethers.Contract(governanceAddress, governanceAbi, provider) : null),
    []
  );

  const refresh = useCallback(async () => {
    if (!governance) {
      setLoading(false);
      return;
    }
    try {
      const [
        count,
        quorum,
        totalWeight,
        majorityPct,
        votingPeriod,
        timelock,
        criticalTimelock,
        executionWindow,
        stakeAge,
        threshold,
        guardian,
        guardianUntil,
        guardianActive,
        power,
        staked,
      ] = await Promise.all([
        governance.proposalCount(),
        governance.QUORUM_WEIGHT(),
        governance.TOTAL_VOTING_WEIGHT(),
        governance.MAJORITY_PERCENT(),
        governance.VOTING_PERIOD(),
        governance.TIMELOCK_DELAY(),
        governance.CRITICAL_TIMELOCK_DELAY(),
        governance.EXECUTION_WINDOW(),
        governance.MIN_STAKE_AGE(),
        governance.PROPOSAL_THRESHOLD(),
        governance.guardian(),
        governance.guardianUntil(),
        governance.guardianActive(),
        address ? settle(governance.getVotingPower(address), ethers.constants.Zero) : ethers.constants.Zero,
        address ? settle(governance.getStakedVotingPower(address), ethers.constants.Zero) : ethers.constants.Zero,
      ]);
      setRules({
        quorum: quorum.toNumber(),
        totalWeight: totalWeight.toNumber(),
        majorityPct: majorityPct.toNumber(),
        votingDays: days(votingPeriod),
        timelockDays: days(timelock),
        criticalDays: days(criticalTimelock),
        executionDays: days(executionWindow),
        stakeAgeDays: days(stakeAge),
        threshold: threshold.toNumber(),
        guardian: guardianActive ? guardian : null,
        guardianUntil: guardianUntil.toNumber(),
      });
      setVotingPower(power.toNumber());
      setStakedPower(staked.toNumber());

      const ids = [];
      for (let id = count.toNumber(); id >= 1 && ids.length < PAGE_SIZE; id--) ids.push(id);
      const loaded = await Promise.all(
        ids.map(async (id) => {
          const [p, state, votable, myVote] = await Promise.all([
            governance.getProposal(id),
            governance.state(id),
            address ? settle(governance.getVotableNodes(id, address), []) : [],
            address ? settle(governance.voteOf(id, address), 0) : 0,
          ]);
          return {
            id,
            proposer: p.proposer,
            target: p.target,
            data: p.data,
            description: p.description,
            startTime: p.startTime.toNumber(),
            endTime: p.endTime.toNumber(),
            executableAt: p.executableAt.toNumber(),
            level: CALL_LEVELS[p.level] || 'standard',
            yes: p.yesVotes.toNumber(),
            no: p.noVotes.toNumber(),
            quorum: p.quorum.toNumber(),
            snapshotWeight: p.snapshotWeight.toNumber(),
            state: STATES[state],
            votableNodes: votable.map(Number),
            myVote: Number(myVote),
          };
        })
      );
      setProposals(loaded);
    } catch (err) {
      console.error('[Governance] read failed', err);
    } finally {
      setLoading(false);
    }
  }, [governance, address]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  /** Level of a call a proposal would make: 'none' (not allowed), 'standard' or 'critical' */
  const callLevelOf = useCallback(
    async (target, data) => {
      if (!governance || !target || !data) return 'none';
      const level = await settle(governance.callLevelOf(target, data), 0);
      return CALL_LEVELS[level] || 'none';
    },
    [governance]
  );

  /** Sends a governance transaction with a progress dialog, then refreshes */
  const send = useCallback(
    async (functionName, args, successText) => {
      try {
        lbDialog.fire({
          title: t('gov.sending'),
          text: t('gov.waitConfirm'),
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => lbDialog.showLoading(),
        });
        const hash = await writeContractAsync({ address: governanceAddress, abi: governanceAbi, functionName, args });
        const receipt = await provider.waitForTransaction(hash);
        if (!receipt.status) throw new Error('Transaction failed on chain');
        lbDialog.fire({ title: t('gov.done'), text: successText, icon: 'success', timer: 2500, showConfirmButton: false });
      } catch (err) {
        console.error(`[Governance] ${functionName} failed`, err);
        lbDialog.fire(t('tx.error'), err.shortMessage || err.message, 'error');
      } finally {
        await refresh();
      }
    },
    [writeContractAsync, refresh, t]
  );

  const isGuardian = Boolean(rules?.guardian && address && rules.guardian.toLowerCase() === address.toLowerCase());

  return {
    enabled: Boolean(governance),
    loading,
    rules,
    votingPower,
    stakedPower,
    isGuardian,
    proposals,
    refresh,
    callLevelOf,
    createProposal: (target, data, description) =>
      send('createProposal', [target, 0n, data, description], t('gov.created')),
    vote: (id, nodes, support) => send('vote', [BigInt(id), nodes, support], t('gov.voted')),
    execute: (id) => send('finalizeProposal', [BigInt(id)], t('gov.executed')),
    cancel: (id) => send('cancelProposal', [BigInt(id)], t('gov.canceledMsg')),
    veto: (id) => send('veto', [BigInt(id)], t('gov.vetoedMsg')),
    transferGuardian: (to) => send('transferGuardian', [to], t('gov.guardianMoved')),
  };
}

export default useGovernance;
